import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faEdit, 
  faTrashAlt, 
  faEye, 
  faPlus,
  faPercent,
  faToggleOn,
  faToggleOff
} from '@fortawesome/free-solid-svg-icons';
import adminService from '../../services/adminService';

function DiscountsList() {
  const [discounts, setDiscounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    status: '',
    applicableTo: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [discountToDelete, setDiscountToDelete] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchDiscounts();
  }, [currentPage, filter]);
  
  const fetchDiscounts = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Build params object for API request
      const params = {
        page: currentPage
      };
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      if (filter.status) {
        params.status = filter.status;
      }
      
      if (filter.applicableTo) {
        params.category = filter.applicableTo;
      }
      
      // Use adminService instead of direct axios calls
      const data = await adminService.getDiscounts(params);
      
      setDiscounts(data.items || []);
      setTotalPages(data.total_pages || 1);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching discounts:', error);
      setError('Failed to fetch discounts. Please try again later.');
      setIsLoading(false);
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on search
    fetchDiscounts();
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({
      ...filter,
      [name]: value
    });
    setCurrentPage(1); // Reset to first page on filter change
  };
  
  const handleDelete = (discount) => {
    setDiscountToDelete(discount);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async () => {
    try {
      // Use adminService instead of direct axios call
      await adminService.deleteDiscount(discountToDelete.id);
      
      // Remove discount from state
      setDiscounts(discounts.filter(discount => discount.id !== discountToDelete.id));
      setShowDeleteModal(false);
      setDiscountToDelete(null);
    } catch (error) {
      console.error('Error deleting discount:', error);
      setError('Failed to delete discount. Please try again.');
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDiscountToDelete(null);
  };
  
  const toggleDiscountStatus = async (discount) => {
    try {
      const newStatus = discount.status === 'active' ? 'inactive' : 'active';
      
      // Use adminService instead of direct axios call
      await adminService.updateDiscount(discount.id, { status: newStatus });
      
      // Update discount in state
      setDiscounts(discounts.map(item => {
        if (item.id === discount.id) {
          return {
            ...item,
            status: newStatus
          };
        }
        return item;
      }));
    } catch (error) {
      console.error('Error updating discount status:', error);
      setError('Failed to update discount status. Please try again.');
    }
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span 
            style={{
              backgroundColor: '#d4edda',
              color: '#155724',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.875rem'
            }}
          >
            Active
          </span>
        );
      case 'inactive':
        return (
          <span 
            style={{
              backgroundColor: '#f8d7da',
              color: '#721c24',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.875rem'
            }}
          >
            Inactive
          </span>
        );
      default:
        return status;
    }
  };
  
  const getApplicableToDisplay = (applicableTo) => {
    switch (applicableTo) {
      case 'membership':
        return 'Membership Plans';
      case 'listing':
        return 'Professional Listing';
      case 'program':
        return 'Programs';
      default:
        return applicableTo;
    }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Discount Codes</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard" className="admin-breadcrumb-link">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item active">
            <span className="admin-breadcrumb-link">Discounts</span>
          </li>
        </ul>
      </div>
      
      {error && (
        <div className="admin-alert admin-alert-danger" style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }}>
          {error}
        </div>
      )}
      
      <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-card-body" style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ position: 'relative', marginRight: '1rem' }}>
                <input 
                  type="text" 
                  placeholder="Search discount codes..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ 
                    padding: '0.5rem 0.5rem 0.5rem 2rem', 
                    borderRadius: '4px', 
                    border: '1px solid var(--admin-border)',
                    minWidth: '250px'
                  }}
                />
                <FontAwesomeIcon 
                  icon={faSearch} 
                  style={{ 
                    position: 'absolute', 
                    left: '0.5rem', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#6c757d'
                  }}
                />
              </div>
              <button 
                type="submit"
                className="admin-btn admin-btn-primary"
                style={{ padding: '0.5rem 1rem' }}
              >
                Search
              </button>
            </form>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <select
                name="status"
                value={filter.status}
                onChange={handleFilterChange}
                style={{ 
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  border: '1px solid var(--admin-border)'
                }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              
              <select
                name="applicableTo"
                value={filter.applicableTo}
                onChange={handleFilterChange}
                style={{ 
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  border: '1px solid var(--admin-border)'
                }}
              >
                <option value="">All Types</option>
                <option value="membership">Membership</option>
                <option value="listing">Professional Listing</option>
                <option value="program">Programs</option>
              </select>
              
              <Link 
                to="/admin/discounts/add" 
                className="admin-btn admin-btn-primary" 
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add Discount
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      <div className="admin-table-container">
        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Discount %</th>
                <th>Applicable To</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {discounts.length > 0 ? (
                discounts.map((discount) => (
                  <tr key={discount.id}>
                    <td style={{ fontWeight: '600' }}>{discount.name}</td>
                    <td>{discount.description}</td>
                    <td>
                      <span style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        fontWeight: '600',
                        color: '#28a745'
                      }}>
                        {discount.value}%
                        <FontAwesomeIcon icon={faPercent} style={{ marginLeft: '4px', fontSize: '0.75rem' }} />
                      </span>
                    </td>
                    <td>{getApplicableToDisplay(discount.applicableTo)}</td>
                    <td>{getStatusBadge(discount.status)}</td>
                    <td>
                      <div className="admin-table-actions">
                        <Link 
                          to={`/admin/discounts/${discount.id}`} 
                          className="admin-table-btn admin-table-btn-view"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Link>
                        <Link 
                          to={`/admin/discounts/${discount.id}/edit`} 
                          className="admin-table-btn admin-table-btn-edit"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Link>
                        <button 
                          className="admin-table-btn admin-table-btn-toggle"
                          onClick={() => toggleDiscountStatus(discount)}
                          title={discount.status === 'active' ? 'Deactivate' : 'Activate'}
                          style={{ 
                            backgroundColor: discount.status === 'active' ? '#fff3cd' : '#d4edda',
                            color: discount.status === 'active' ? '#856404' : '#155724'
                          }}
                        >
                          <FontAwesomeIcon icon={discount.status === 'active' ? faToggleOn : faToggleOff} />
                        </button>
                        <button 
                          className="admin-table-btn admin-table-btn-delete"
                          onClick={() => handleDelete(discount)}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                    No discounts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        
        {totalPages > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            padding: '1rem', 
            borderTop: '1px solid var(--admin-border)'
          }}>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button 
                className="admin-btn"
                style={{ 
                  backgroundColor: '#f8f9fa', 
                  color: 'var(--admin-dark)',
                  padding: '0.5rem 0.75rem'
                }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(page => Math.max(page - 1, 1))}
              >
                &laquo;
              </button>
              
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i}
                  className="admin-btn"
                  style={{ 
                    backgroundColor: currentPage === i + 1 ? 'var(--admin-primary)' : '#f8f9fa',
                    color: currentPage === i + 1 ? 'white' : 'var(--admin-dark)',
                    padding: '0.5rem 0.75rem'
                  }}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              
              <button 
                className="admin-btn"
                style={{ 
                  backgroundColor: '#f8f9fa',
                  color: 'var(--admin-dark)',
                  padding: '0.5rem 0.75rem'
                }}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(page => Math.min(page + 1, totalPages))}
              >
                &raquo;
              </button>
            </div>
          </div>
        )}
      </div>
      
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1050
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            width: '100%',
            maxWidth: '400px',
            boxShadow: 'var(--admin-shadow)'
          }}>
            <h2 style={{ marginBottom: '1rem' }}>Confirm Deletion</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              Are you sure you want to delete the discount code "{discountToDelete.code}"? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button 
                className="admin-btn admin-btn-light"
                onClick={cancelDelete}
                style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6' }}
              >
                Cancel
              </button>
              <button 
                className="admin-btn admin-btn-danger"
                onClick={confirmDelete}
                style={{ backgroundColor: '#dc3545', color: 'white' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default DiscountsList;
